import { type ArcSection, type Elevation, elevationProfile, elevationV3, slopeAndCurvature } from './elevation';
import { spacedCycleForDate } from './era';
import type { EraId } from '../config/eras';
import { DEFAULT_PHYSICS, eraPhysics, HANDLING_2_FROM, HANDLING_31_FROM, HANDLING_32_FROM, HANDLING_33_FROM, HANDLING_34_FROM, HANDLING_3_FROM, MEDALS, type PhysicsTuning, TRACK, TRACK_V3 } from '../config/tuning';
import { dcos, dsin, PI, TWO_PI } from '../sim/dmath';
import { buildRuntime, type TrackRuntime } from '../sim/trackQuery';
import {
  buildCenterline,
  type Centerline,
  cyclicDist,
  findCorners,
  findStraights,
  forRange,
  minFilterCyclic,
  selfIntersects,
  sideClearance,
  smoothCyclic,
} from './analyze';
import { botReach, type BotRun, makePlan, runBotSuite } from './bot';
import { planAprons, type RunoffStyle, runoffStyle } from './runoff';
import { arcDist, bridgeHalf, type Crossing, crossingDip, findCrossings, type SegmentCrossing, underpassHalf } from './crossing';
import { type LayoutFamily, makeLayoutV3, type Signature, SIGNATURES } from './layouts';
import { type DailySpec, defaultSpec, simHooksFor, validateModifiers } from './modifiers';
import { generateCircuitName } from './names';
import { settingOfCircuit } from './setting';
import { circuitDate } from './calendar';
import { eraForDate, rotationEra } from './era';
import { hashHex, Rng } from './prng';
import { computeRacingLine, type RacingLine } from './racingLine';
import { resampleClosed, sampleClosedCatmullRom } from './spline';
import {
  type BrakingZone,
  type Corner,
  type MedalTimes,
  type Straight,
  Surface,
  type TrackData,
  type TrackFeatures,
  type Vec2,
} from './types';

export const GENERATOR_VERSION = 3;
/**
 * Circuits from this day on come from generator v3 (corner modules, figures
 * of eight). Earlier days keep generator v2's circuits exactly, so their
 * records and boards stay valid.
 */
export const V3_FROM = '2026-09-29';

export function generatorVersion(date: string): number {
  return date >= V3_FROM ? 3 : 2;
}

/**
 * Circuits from this day on get runoff aprons where the bot runs to the white
 * line, and runoff dressed by era (see runoff.ts). Earlier days keep theirs.
 */
export const RUNOFF_FROM = '2026-09-30';

/** The day's showpiece (v3): a seeded shuffle of all twelve per cycle, so none comes back within days. */
export function signatureForDate(date: string): Signature {
  return signatureOfCircuit(circuitDate(date));
}

/** The showpiece a circuit was made with, by the date it was made for (a swapped day races another's: calendar.ts). */
export function signatureOfCircuit(date: string): Signature {
  return spacedCycleForDate(SIGNATURES, 'signature', date, 8, V3_FROM);
}

/** Lap-time window the bot's lap must fall in, per generator version. */
export function lapWindow(version: number): { min: number; max: number } {
  return version >= 3 ? { min: TRACK_V3.lapTimeMin, max: TRACK_V3.lapTimeMax } : { min: TRACK.lapTimeMin, max: TRACK.lapTimeMax };
}

export interface GeneratedCircuit {
  date: string;
  salt: number;
  spec: DailySpec;
  attempt: number;
  archetype: Archetype;
  /** The day's car and the exact physics every lap on this circuit uses. */
  era: EraId;
  physics: PhysicsTuning;
  name: string;
  controlPoints: Vec2[];
  track: TrackData;
  runtime: TrackRuntime;
  line: RacingLine;
  /** The bot's best clean lap: proves the track, sets the author time. */
  bot: BotRun;
  medals: MedalTimes;
  /** Fingerprint of the exact geometry; lap records are tied to it. */
  trackHash: string;
  stats: GenerationStats;
}

export interface GenerationStats {
  attempts: number;
  rejections: Record<string, number>;
  botRuns: number;
  millis: number;
}

export class Rejected extends Error {
  constructor(public reason: string) {
    super(reason);
  }
}

// ---------------------------------------------------------------------------
// Control points
// ---------------------------------------------------------------------------

/**
 * 8-12 jittered control points around a centre, laid out as a loop with a slot
 * for every feature a circuit needs: the long straight (three collinear
 * points), a heavy-braking end (a hairpin or a tight corner), a back section
 * carrying a chicane, and a fast sweeper that feeds the straight. Every point
 * is jittered, the loop can be mirrored and rotated, and validation decides
 * what survives.
 */
export type Archetype = 'D' | 'teardrop' | LayoutFamily;

export function makeControlPoints(rng: Rng, scale = 1): Vec2[] {
  return makeLayout(rng, scale).pts;
}

export function makeLayout(rng: Rng, scale = 1): { pts: Vec2[]; archetype: Archetype } {
  const archetype: Archetype = rng.chance(0.5) ? 'D' : 'teardrop';
  const pts = archetype === 'D' ? loopD(rng) : loopTeardrop(rng);
  // Centre the loop, maybe mirror it (clockwise), then rotate it.
  let cx = 0;
  let cy = 0;
  for (const p of pts) {
    cx += p.x / pts.length;
    cy += p.y / pts.length;
  }
  const mirror = rng.chance(0.5) ? -1 : 1;
  const rot = rng.range(0, TWO_PI);
  const cr = dcos(rot);
  const sr = dsin(rot);
  return {
    archetype,
    pts: pts.map((p) => {
      const x = (p.x - cx) * scale;
      const y = (p.y - cy) * mirror * scale;
      return { x: x * cr - y * sr, y: x * sr + y * cr };
    }),
  };
}

/**
 * "D" loop: the straight runs into a hairpin, the top section carries the
 * chicane, and a big sweeper brings you back onto the straight.
 */
function loopD(rng: Rng): Vec2[] {
  const Ls = rng.range(255, 320);
  const H = rng.range(190, 255);
  const jit = (v: number, a = 4): number => v + rng.range(-a, a);
  const pts: Vec2[] = [];

  // Bottom: the long straight, heading +x (three collinear points).
  pts.push({ x: 0, y: 0 }, { x: Ls * rng.range(0.42, 0.58), y: 0 }, { x: Ls, y: 0 });

  // Right end: a hairpin on a real arc around (Ls, Rc). Ending the arc at 75
  // degrees leaves the exit already pointing up-left, toward the top section.
  const Rc = rng.range(23, 29);
  const hp = (deg: number): Vec2 => {
    const a = (deg * PI) / 180;
    return { x: Ls + Rc * dcos(a), y: Rc + Rc * dsin(a) };
  };
  pts.push(hp(-30), hp(22), hp(75));

  // Top-right corner, then the top section heading -x with the chicane on it.
  pts.push({ x: jit(Ls - rng.range(28, 50), 5), y: jit(H - rng.range(0, 11), 4) });
  const xc = Ls * rng.range(0.34, 0.5);
  const g = rng.range(30, 40);
  const j = rng.range(9.5, 12.5) * (rng.chance(0.5) ? 1 : -1);
  pts.push({ x: xc + g / 2, y: H + j });
  pts.push({ x: xc - g / 2, y: H - j });

  // Left: a fast sweeper on a circular arc that lands tangent onto the straight at A.
  const R = H / 2;
  const circ = (deg: number): Vec2 => {
    const a = (deg * PI) / 180;
    return { x: jit(R * dcos(a), 2.5), y: jit(R + R * dsin(a), 2.5) };
  };
  pts.push(circ(100), circ(155), circ(212));
  return pts;
}

/**
 * "Teardrop" loop: the straight ends in a tight 90-degree corner (the heavy
 * braking) that sweeps up to the top section and its chicane; a long diagonal
 * runs down to a hairpin at the tail, which feeds the straight.
 */
function loopTeardrop(rng: Rng): Vec2[] {
  const Ls = rng.range(265, 330);
  const H = rng.range(165, 215);
  const jit = (v: number, a = 4): number => v + rng.range(-a, a);
  const pts: Vec2[] = [];
  // Bottom: the long straight.
  pts.push({ x: 0, y: 0 }, { x: Ls * rng.range(0.42, 0.58), y: 0 }, { x: Ls, y: 0 });
  // Right end: a tight 90 (the heavy braking) sweeping up into a fast corner at the top.
  const rc = rng.range(28, 34);
  pts.push({ x: Ls + rc * 0.85, y: rc * 0.42 });
  const rs = rng.range(88, 115);
  pts.push({ x: jit(Ls - rs * 0.25, 4), y: jit(H, 3) });
  // Top: the chicane, then a corner that turns down onto the long diagonal.
  const xc = Ls * rng.range(0.42, 0.56);
  const g = rng.range(30, 40);
  const j = rng.range(9.5, 12.5) * (rng.chance(0.5) ? 1 : -1);
  pts.push({ x: xc + g / 2, y: H + j });
  pts.push({ x: xc - g / 2, y: H - j });
  pts.push({ x: jit(xc - g / 2 - rng.range(66, 94), 4), y: jit(H - rng.range(15, 28), 4) });
  // The diagonal levels out before the tail, so the hairpin is a full U-turn.
  const Rh = rng.range(23, 28);
  const hx = rng.range(33, 55);
  pts.push({ x: jit(rng.range(22, 50), 4), y: 2 * Rh + rng.range(8, 17) });
  const hp = (deg: number): Vec2 => {
    const a = (deg * PI) / 180;
    return { x: -hx + Rh * dcos(a), y: Rh + Rh * dsin(a) };
  };
  pts.push(hp(115), hp(180), hp(245));
  return pts;
}

// ---------------------------------------------------------------------------
// Feature detection
// ---------------------------------------------------------------------------

/**
 * Two opposite-direction curvature peaks close together: a quick left-right
 * (or right-left). Each lobe is measured only around its own apex, and must be
 * neither too big nor hairpin-tight (a hairpin exit plus a kink is not a chicane).
 */
function findChicane(cl: Centerline): [Corner, Corner] | null {
  const n = cl.n;
  const ds = cl.length / n;
  const k = cl.kappa;
  const kMin = 1 / TRACK.chicaneMaxRadius;
  const peaks: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = Math.abs(k[i]);
    if (a < kMin) continue;
    if (a >= Math.abs(k[(i - 1 + n) % n]) && a > Math.abs(k[(i + 1) % n])) peaks.push(i);
  }
  const lobe = (apex: number): Corner => {
    const dir = k[apex] > 0 ? 1 : -1;
    let a = apex;
    let b = apex;
    // A lobe runs while it keeps curving the same way, up to 30 m each side of its apex.
    const cap = Math.round(30 / ds);
    while (dir * k[(a - 1 + n) % n] > kMin * 0.35 && dir * k[(a - 1 + n) % n] <= dir * k[a] + 1e-9 && (apex - a + n) % n < cap) a = (a - 1 + n) % n;
    while (dir * k[(b + 1) % n] > kMin * 0.35 && dir * k[(b + 1) % n] <= dir * k[b] + 1e-9 && (b - apex + n) % n < cap) b = (b + 1) % n;
    let angle = 0;
    forRange(a, b, n, (i) => (angle += k[i] * ds));
    return { start: a, end: b, apex, dir, angle, minRadius: 1 / Math.abs(k[apex]), length: (((b - a + n) % n) + 1) * ds };
  };
  let best: [Corner, Corner] | null = null;
  let bestScore = -Infinity;
  for (let q = 0; q < peaks.length; q++) {
    const p1 = peaks[q];
    const p2 = peaks[(q + 1) % peaks.length];
    if (p1 === p2 || k[p1] * k[p2] >= 0) continue;
    const gap = ((p2 - p1 + n) % n) * ds;
    if (gap > TRACK.chicaneMaxGap) continue;
    const c1 = lobe(p1);
    const c2 = lobe(p2);
    const a1 = Math.abs(c1.angle);
    const a2 = Math.abs(c2.angle);
    if (a1 < TRACK.chicaneMinAngle || a2 < TRACK.chicaneMinAngle) continue;
    if (a1 > TRACK.chicaneMaxLobe || a2 > TRACK.chicaneMaxLobe) continue;
    if (c1.minRadius < TRACK.chicaneMinRadius || c2.minRadius < TRACK.chicaneMinRadius) continue;
    // Prefer the tightest, most balanced pair.
    const score = Math.min(a1, a2) - gap * 0.004;
    if (score > bestScore) {
      bestScore = score;
      best = [c1, c2];
    }
  }
  return best;
}

/** A long run curving one way at a fast radius. */
function findSweeper(cl: Centerline): Corner | null {
  const n = cl.n;
  const ds = cl.length / n;
  const k = cl.kappa;
  const lo = 1 / TRACK.sweeperMaxRadius;
  const hi = 1 / TRACK.sweeperMinRadius;
  let best: Corner | null = null;
  for (const dir of [1, -1]) {
    const ok = (i: number): boolean => dir * k[i] >= lo && dir * k[i] <= hi;
    let start = -1;
    for (let i = 0; i < n; i++) {
      if (!ok(i)) {
        start = i;
        break;
      }
    }
    if (start < 0) continue;
    let runStart = -1;
    let angle = 0;
    let maxK = 0;
    for (let q = 1; q <= n; q++) {
      const i = (start + q) % n;
      if (ok(i)) {
        if (runStart < 0) {
          runStart = i;
          angle = 0;
          maxK = 0;
        }
        angle += k[i] * ds;
        maxK = Math.max(maxK, Math.abs(k[i]));
      } else if (runStart >= 0) {
        const end = (i - 1 + n) % n;
        if (Math.abs(angle) >= TRACK.sweeperMinAngle && (!best || Math.abs(angle) > Math.abs(best.angle))) {
          best = { start: runStart, end, apex: runStart, dir, angle, minRadius: 1 / maxK, length: (((end - runStart + n) % n) + 1) * ds };
        }
        runStart = -1;
      }
    }
  }
  return best;
}

function computeBrakingZones(cl: Centerline, corners: Corner[], profile: number[]): BrakingZone[] {
  const n = cl.n;
  const ds = cl.length / n;
  const back = Math.round(220 / ds);
  const zones: BrakingZone[] = [];
  for (const c of corners) {
    if (Math.abs(c.angle) < 0.25) continue;
    let vMin = Infinity;
    forRange(c.start, c.end, n, (i) => {
      if (profile[i] < vMin) vMin = profile[i];
    });
    let vMax = 0;
    for (let k = 0; k <= back; k++) {
      const v = profile[(c.start - k + n) % n];
      if (v > vMax) vMax = v;
    }
    const drop = vMax - vMin;
    if (drop < 10) continue;
    zones.push({ cornerIndex: c.start, speedDrop: drop, entrySpeed: vMax, cornerSpeed: vMin, dir: c.dir, major: drop >= 18 });
  }
  return zones;
}

function detectFeatures(cl: Centerline, corners: Corner[], straights: Straight[], zones: BrakingZone[]): TrackFeatures {
  const n = cl.n;
  const ds = cl.length / n;
  const longStraight = straights[0];
  if (!longStraight || longStraight.length < TRACK.longStraightMin) throw new Rejected('no-long-straight');
  // The corner the long straight runs into must need heavy braking.
  let heavy: BrakingZone | null = null;
  for (const z of zones) {
    const after = (z.cornerIndex - longStraight.end + n) % n;
    if (after * ds <= 80 && (!heavy || z.speedDrop > heavy.speedDrop)) heavy = z;
  }
  if (!heavy || heavy.speedDrop < TRACK.heavyBrakingDrop) throw new Rejected('no-heavy-braking');
  const hairpin = corners.find((c) => Math.abs(c.angle) >= TRACK.hairpinAngle && c.minRadius <= TRACK.hairpinMaxRadius);
  if (!hairpin) throw new Rejected('no-hairpin');
  const chicane = findChicane(cl);
  if (!chicane) throw new Rejected('no-chicane');
  const sweeper = findSweeper(cl);
  if (!sweeper) throw new Rejected('no-sweeper');
  return { longStraight, heavyBraking: heavy, hairpin, chicane, sweeper };
}

/**
 * Generator v3: the long straight into heavy braking is still a must; the rest
 * is whatever the day's corners make (a hairpin, a chicane, a sweeper, esses).
 */
function detectFeaturesV3(cl: Centerline, corners: Corner[], straights: Straight[], zones: BrakingZone[]): TrackFeatures {
  const n = cl.n;
  const ds = cl.length / n;
  const longStraight = straights[0];
  if (!longStraight || longStraight.length < TRACK_V3.longStraightMin) throw new Rejected('no-long-straight');
  let heavy: BrakingZone | null = null;
  for (const z of zones) {
    const after = (z.cornerIndex - longStraight.end + n) % n;
    if (after * ds <= 80 && (!heavy || z.speedDrop > heavy.speedDrop)) heavy = z;
  }
  if (!heavy || heavy.speedDrop < TRACK_V3.heavyBrakingDrop) throw new Rejected('no-heavy-braking');
  const hairpin = corners.find((c) => Math.abs(c.angle) >= TRACK.hairpinAngle && c.minRadius <= TRACK.hairpinMaxRadius) ?? null;
  return { longStraight, heavyBraking: heavy, hairpin, chicane: findChicane(cl), sweeper: findSweeper(cl), esses: findEsses(cl, corners) };
}

/** Three or more fast bends in a row, each turning the other way from the last. */
function findEsses(cl: Centerline, corners: Corner[]): Corner[] | null {
  const n = cl.n;
  const ds = cl.length / n;
  const fast = (c: Corner): boolean => c.minRadius >= TRACK_V3.essesMinRadius && Math.abs(c.angle) >= TRACK_V3.essesMinAngle;
  let best: Corner[] | null = null;
  for (let a = 0; a < corners.length; a++) {
    const run = [corners[a]];
    if (!fast(corners[a])) continue;
    for (let b = a + 1; b < a + corners.length; b++) {
      const prev = run[run.length - 1];
      const next = corners[b % corners.length];
      const gap = ((next.start - prev.end + n) % n) * ds;
      if (!fast(next) || next.dir === prev.dir || gap > 40) break;
      run.push(next);
    }
    if (run.length >= 3 && (!best || run.length > best.length)) best = run;
  }
  return best;
}

// ---------------------------------------------------------------------------
// Track data (curbs, runoff, barriers, sectors, spawn)
// ---------------------------------------------------------------------------

function buildTrackData(
  cl: Centerline,
  corners: Corner[],
  straights: Straight[],
  zones: BrakingZone[],
  features: TrackFeatures,
  profile: number[],
  hills: Elevation,
  version = 2,
  crossing: Crossing | null = null,
  lineOffset: number[] | null = null,
  extra: { bank?: number[] | null; sections?: ArcSection[]; signature?: string; runoffStyle?: RunoffStyle } = {},
): TrackData {
  const n = cl.n;
  const ds = cl.length / n;
  const hw = TRACK.halfWidth;

  // Curbs on the inside of every real corner, and on the outside of its exit.
  const curbLeft = new Array<number>(n).fill(0);
  const curbRight = new Array<number>(n).fill(0);
  for (const c of corners) {
    if (Math.abs(c.angle) < 0.22 || c.minRadius > TRACK.curbRadius) continue;
    const inside = c.dir > 0 ? curbLeft : curbRight;
    const outside = c.dir > 0 ? curbRight : curbLeft;
    forRange((c.start - 3 + n) % n, (c.end + 3) % n, n, (i) => (inside[i] = 1));
    forRange(c.apex, (c.end + Math.round(20 / ds)) % n, n, (i) => (outside[i] = 1));
  }

  // Runoff: grass everywhere, gravel traps on the outside of braking zones.
  const runL = new Array<number>(n).fill(TRACK.runoffGrass);
  const runR = new Array<number>(n).fill(TRACK.runoffGrass);
  const surfL = new Array<number>(n).fill(Surface.Grass);
  const surfR = new Array<number>(n).fill(Surface.Grass);
  // By era (circuits from RUNOFF_FROM): old circuits only trap the big stops (in sand); modern ones
  // pave the big stops with painted tarmac and keep gravel for the rest.
  const style = extra.runoffStyle;
  for (const z of zones) {
    const c = corners.find((k) => k.start === z.cornerIndex);
    if (!c) continue;
    if (style === 'classic' && !z.major) continue;
    const trap = style === 'modern' && z.major ? Surface.Runoff : Surface.Gravel;
    const run = z.dir > 0 ? runR : runL;
    const surf = z.dir > 0 ? surfR : surfL;
    forRange((c.start - Math.round(30 / ds) + n) % n, (c.end + Math.round(12 / ds)) % n, n, (i) => {
      run[i] = TRACK.runoffGravel;
      surf[i] = trap;
    });
  }

  // Barriers: never closer to another part of the circuit than half the gap,
  // never past the centre of curvature on the inside of a corner.
  const clear = sideClearance(cl, TRACK.clearanceArc, crossing ? crossingIgnore(cl, crossing) : undefined);
  const limL = new Array<number>(n);
  const limR = new Array<number>(n);
  // At a crossover: the cutting's walls along the lower road, the parapets on the bridge.
  // The street section runs between walls; a banked curve has a wall along its top.
  const walled = new Array<boolean>(n).fill(false);
  const inSection = (name: string, s: number): boolean =>
    (extra.sections ?? []).some((q) => q.name === name && ((q.from <= q.to && s >= q.from && s <= q.to) || (q.from > q.to && (s >= q.from || s <= q.to))));
  const bank = extra.bank ?? null;
  for (let i = 0; i < n; i++) {
    let l = hw + runL[i];
    let r = hw + runR[i];
    l = Math.min(l, clear.left[i] / 2 - 1);
    r = Math.min(r, clear.right[i] / 2 - 1);
    const k = cl.kappa[i];
    if (k > 1e-4) l = Math.min(l, 1 / k - 2);
    if (k < -1e-4) r = Math.min(r, -1 / k - 2);
    if (crossing) {
      if (crossingDip(crossing, cl.s[i], cl.length) >= 0.25) {
        l = Math.min(l, crossing.cutHalf - 0.5);
        r = Math.min(r, crossing.cutHalf - 0.5);
        walled[i] = true;
      }
      if (arcDist(cl.s[i], crossing.sOver, cl.length) <= bridgeHalf(crossing)) {
        l = Math.min(l, crossing.deckHalf - 0.3);
        r = Math.min(r, crossing.deckHalf - 0.3);
        walled[i] = true;
      }
    }
    if (version >= 3 && inSection('streets', cl.s[i])) {
      l = Math.min(l, hw + TRACK_V3.streetRunoff);
      r = Math.min(r, hw + TRACK_V3.streetRunoff);
      walled[i] = true;
    }
    if (bank && Math.abs(bank[i]) > 0.08) {
      // The raised side (outside) ends at the wall on top of the bank.
      if (bank[i] > 0) l = Math.min(l, hw + TRACK_V3.bankRunoff);
      else r = Math.min(r, hw + TRACK_V3.bankRunoff);
      walled[i] = true;
    }
    limL[i] = l;
    limR[i] = r;
  }
  const win = Math.round(10 / ds);
  const barrierLeft = smoothCyclic(minFilterCyclic(limL, win), win);
  const barrierRight = smoothCyclic(minFilterCyclic(limR, win), win);
  for (let i = 0; i < n; i++) {
    // Walls and parapets are meant to be close; nowhere else may be.
    let nearWall = false;
    for (let k = -2 * win; k <= 2 * win && !nearWall; k++) nearWall = walled[(i + k + n) % n];
    if (nearWall) continue;
    if (barrierLeft[i] - hw < TRACK.runoffMin || barrierRight[i] - hw < TRACK.runoffMin) throw new Rejected('cramped');
  }

  const ls = features.longStraight;
  let spawnIndex = (ls.start + 4) % n;
  if (version >= 3 && lineOffset) {
    // Where the racing line crosses the middle of the road: the car starts straight and on line.
    const before = (n - Math.round(40 / ds)) % n;
    let best = spawnIndex;
    for (let i = spawnIndex; i !== before; i = (i + 1) % n) {
      if (Math.abs(lineOffset[i]) < Math.abs(lineOffset[best]) - 0.05) best = i;
      if (Math.abs(lineOffset[i]) < 0.4) break;
    }
    spawnIndex = best;
  }
  const spawnDistance = cl.length - cl.s[spawnIndex];
  const spawnSpeed = Math.floor(Math.min(profile[spawnIndex] * 0.97, 44) * 2) / 2;
  const spawnOffset = version >= 3 && lineOffset ? Math.round(Math.max(-(hw - 1.5), Math.min(hw - 1.5, lineOffset[spawnIndex])) * 10) / 10 : undefined;

  return {
    version,
    n,
    length: cl.length,
    halfWidth: hw,
    curbWidth: TRACK.curbWidth,
    px: cl.px,
    py: cl.py,
    tx: cl.tx,
    ty: cl.ty,
    s: cl.s,
    curvature: cl.kappa,
    curbLeft,
    curbRight,
    runoffLeft: surfL,
    runoffRight: surfR,
    barrierLeft,
    barrierRight,
    sectorIndex: [Math.round(n / 3), Math.round((2 * n) / 3)],
    corners,
    straights,
    brakingZones: zones,
    features,
    spawnDistance,
    spawnSpeed,
    ...(spawnOffset !== undefined ? { spawnOffset } : {}),
    elev: hills.elev,
    terrain: hills.kind,
    crossing,
    ...(bank ? { bank } : {}),
    ...(extra.signature ? { signature: extra.signature, sections: extra.sections ?? [] } : {}),
    ...(style ? { runoffStyle: style } : {}),
  };
}

/** Clearance checks skip the two passes of a crossover against each other near the crossing. */
function crossingIgnore(cl: Centerline, c: { over: number; under: number }): (i: number, j: number) => boolean {
  const n = cl.n;
  const zone = Math.round(45 / (cl.length / n));
  return (i, j) =>
    (cyclicDist(i, c.over, n) <= zone && cyclicDist(j, c.under, n) <= zone) || (cyclicDist(i, c.under, n) <= zone && cyclicDist(j, c.over, n) <= zone);
}

// ---------------------------------------------------------------------------
// One attempt
// ---------------------------------------------------------------------------

interface Candidate {
  cps: Vec2[];
  archetype: Archetype;
  crossing?: Crossing | null;
  bank?: number[] | null;
  sections?: ArcSection[];
  signature?: string;
  cl: Centerline;
  corners: Corner[];
  straights: Straight[];
  zones: BrakingZone[];
  features: TrackFeatures;
  line: RacingLine;
  profile: number[];
  hills: Elevation;
}

function analyzeLayout(pts: Vec2[], p: PhysicsTuning, version = 2): Omit<Candidate, 'cps' | 'archetype' | 'hills'> {
  const cl = buildCenterline(pts);
  const ds = cl.length / cl.n;
  const corners = findCorners(cl, TRACK.cornerCurvature, Math.round(12 / ds), 0.12);
  const straights = findStraights(cl, TRACK.straightMaxCurvature);
  const line = computeRacingLine(cl, TRACK.halfWidth, 1.15);
  const profile = makePlan(line, p, 0.93).profile;
  const zones = computeBrakingZones(cl, corners, profile);
  const features = version >= 3 ? detectFeaturesV3(cl, corners, straights, zones) : detectFeatures(cl, corners, straights, zones);
  return { cl, corners, straights, zones, features, line, profile };
}

function tryAttempt(rng: Rng, p: PhysicsTuning, scale = 1): Candidate {
  const { pts: cps, archetype } = makeLayout(rng, scale);
  for (let i = 0; i < cps.length; i++) {
    const a = cps[i];
    const b = cps[(i + 1) % cps.length];
    if ((b.x - a.x) * (b.x - a.x) + (b.y - a.y) * (b.y - a.y) < 12 * 12) throw new Rejected('points-too-close');
  }
  const dense = sampleClosedCatmullRom(cps, 80);
  let pts = resampleClosed(dense, TRACK.sampleSpacing);
  const probe = buildCenterline(pts);
  let maxK = 0;
  for (const k of probe.kappa) if (Math.abs(k) > maxK) maxK = Math.abs(k);
  if (maxK > 1 / TRACK.minRadius) throw new Rejected('radius');
  if (selfIntersects(probe.px, probe.py)) throw new Rejected('self-intersection');
  if (sideClearance(probe, TRACK.clearanceArc).min < TRACK.minClearance) throw new Rejected('clearance');

  // Find the long straight and put the start/finish line 40% of the way down it.
  const first = analyzeLayout(pts, p);
  const ls = first.features.longStraight;
  const n = probe.n;
  const startIdx = (ls.start + Math.round(((ls.end - ls.start + n) % n) * 0.4)) % n;
  pts = pts.slice(startIdx).concat(pts.slice(0, startIdx));
  const flat = analyzeLayout(pts, p);
  // The day's hills (usually none), drawn from the same stream once the layout is settled.
  const { cl, corners } = flat;
  const hills = elevationProfile({ n: cl.n, length: cl.length, s: cl.s, corners, features: flat.features }, rng);
  if (hills.kind === 'flat') return { cps, archetype, ...flat, hills };
  const { grade, vcurv } = slopeAndCurvature(hills.elev, cl.length);
  flat.line.grade = grade;
  flat.line.vcurv = vcurv;
  const profile = makePlan(flat.line, p, 0.93).profile;
  const zones = computeBrakingZones(cl, corners, profile);
  return { cps, archetype, ...flat, profile, zones, hills };
}

/**
 * Generator v3: a layout from corner modules (see layouts.ts). A figure of
 * eight must cross itself exactly once, squarely, on straight road, with room
 * for the underpass's ramps away from the start line.
 */
function tryAttemptV3(rng: Rng, p: PhysicsTuning, scale: number, family: LayoutFamily, signature: Signature): Candidate {
  const layout = makeLayoutV3(rng, scale, family, signature);
  if (!layout) throw new Rejected('closure');
  let pts = resampleClosed(layout.pts, TRACK.sampleSpacing);
  const probe = buildCenterline(pts);
  if (probe.length < TRACK_V3.minLength || probe.length > TRACK_V3.maxLength) throw new Rejected('length');
  let maxK = 0;
  for (const k of probe.kappa) if (Math.abs(k) > maxK) maxK = Math.abs(k);
  if (maxK > 1 / TRACK.minRadius) throw new Rejected('radius');
  const crossings = findCrossings(probe.px, probe.py);
  if (layout.family === 'loop' && crossings.length > 0) throw new Rejected('self-intersection');
  if (layout.family === 'figure8' && crossings.length !== 1) throw new Rejected('crossings');
  const ignore = crossings.length ? crossingIgnore(probe, { over: crossings[0].i, under: crossings[0].j }) : undefined;
  if (sideClearance(probe, TRACK.clearanceArc, ignore).min < TRACK.minClearance) throw new Rejected('clearance');

  // The start/finish line 40% of the way down the long straight.
  const first = analyzeLayout(pts, p, 3);
  const ls = first.features.longStraight;
  const n = probe.n;
  const L = probe.length;
  const startIdx = (ls.start + Math.round(((ls.end - ls.start + n) % n) * 0.4)) % n;
  const shift = probe.s[startIdx];
  pts = pts.slice(startIdx).concat(pts.slice(0, startIdx));
  const flat = analyzeLayout(pts, p, 3);
  const { cl, corners } = flat;
  // The layout's named stretches and banking, moved onto the final lap (start line at 0).
  const toArc = (f: number): number => (((f * L - shift) % L) + L) % L;
  const sections: ArcSection[] = layout.sections.map((q) => ({ name: q.name, from: toArc(q.from), to: toArc(q.to) }));
  let bank: number[] | null = null;
  if (layout.bank) {
    const raw = layout.bank;
    const m = raw.length;
    bank = cl.s.map((s) => {
      const q = (((s + shift) % L) / L) * m;
      const i0 = Math.floor(q) % m;
      const i1 = (i0 + 1) % m;
      const f = q - Math.floor(q);
      const b = raw[i0] + (raw[i1] - raw[i0]) * f;
      return Math.round(b * 1e4) / 1e4;
    });
  }
  const crossing = layout.family === 'figure8' ? makeCrossing(cl, rng) : null;
  let hills: Elevation;
  if (crossing) {
    const reserved = [{ at: crossing.sUnder, half: underpassHalf(crossing) + 20 }, { at: crossing.sOver, half: 90 }];
    hills = elevationProfile({ n: cl.n, length: cl.length, s: cl.s, corners, features: flat.features, reserved }, rng);
    hills.elev = hills.elev.map((h, i) => h - crossingDip(crossing, cl.s[i], cl.length));
  } else {
    const v3 = elevationV3({ n: cl.n, length: cl.length, s: cl.s, corners, features: flat.features }, sections, signature, rng);
    if (!v3) throw new Rejected('setpiece-start');
    hills = v3;
  }
  const { grade, vcurv } = slopeAndCurvature(hills.elev, cl.length);
  for (const g of grade) if (Math.abs(g) > TRACK_V3.maxGrade) throw new Rejected('too-steep');
  flat.line.grade = grade;
  flat.line.vcurv = vcurv;
  flat.line.minLoad = 0.45;
  if (bank) flat.line.bank = bank;
  const profile = makePlan(flat.line, p, 0.93).profile;
  const zones = computeBrakingZones(cl, corners, profile);
  return { cps: [], archetype: layout.family, ...flat, profile, zones, hills, crossing, bank, sections, signature };
}

/** Pick which pass goes under and check the crossover fits. */
function makeCrossing(cl: Centerline, rng: Rng): Crossing {
  const found = findCrossings(cl.px, cl.py);
  if (found.length !== 1) throw new Rejected('crossings');
  const c: SegmentCrossing = found[0];
  const n = cl.n;
  const L = cl.length;
  const ds = L / n;
  const sin = Math.abs(cl.tx[c.i] * cl.ty[c.j] - cl.ty[c.i] * cl.tx[c.j]);
  if (sin < TRACK_V3.crossingMinSin) throw new Rejected('crossing-angle');
  // Both passes straight where they cross (no curbs, a simple bridge).
  const reach = Math.round(35 / ds);
  for (const at of [c.i, c.j]) {
    for (let k = -reach; k <= reach; k++) if (Math.abs(cl.kappa[(at + k + n) % n]) > 1 / 150) throw new Rejected('crossing-curve');
  }
  const segS = (i: number, t: number): number => {
    const j = (i + 1) % n;
    const dx = cl.px[j] - cl.px[i];
    const dy = cl.py[j] - cl.py[i];
    return cl.s[i] + Math.sqrt(dx * dx + dy * dy) * t;
  };
  const sI = segS(c.i, c.ti);
  const sJ = segS(c.j, c.tj);
  const depth = TRACK_V3.crossingDepth;
  const make = (iUnder: boolean): Crossing => ({
    over: iUnder ? c.j : c.i,
    under: iUnder ? c.i : c.j,
    sOver: iUnder ? sJ : sI,
    sUnder: iUnder ? sI : sJ,
    x: c.x,
    y: c.y,
    sin,
    depth,
    ramp: Math.max(110, (1.875 * depth) / TRACK_V3.crossingGrade),
    flat,
    cutHalf: TRACK_V3.cutHalf,
    deckHalf: TRACK_V3.deckHalf,
  });
  const flat = rng.range(14, 26);
  // Clear of the level start/finish stretch, and of the bridge itself.
  const fits = (x: Crossing): boolean =>
    arcDist(x.sUnder, 0, L) >= underpassHalf(x) + 112 && arcDist(x.sOver, 0, L) >= 80 && arcDist(x.sUnder, x.sOver, L) >= underpassHalf(x) + 40;
  // Either pass may be the one that dips: the day's pick if it fits, else the other.
  const prefer = rng.chance(0.5);
  let crossing = make(prefer);
  if (!fits(crossing)) crossing = make(!prefer);
  if (!fits(crossing)) throw new Rejected('crossing-start');
  // No tight corners down in the cutting, where the walls are close.
  for (let i = 0; i < n; i++) {
    if (crossingDip(crossing, cl.s[i], L) > 1 && Math.abs(cl.kappa[i]) > 1 / 45) throw new Rejected('crossing-corner');
  }
  return crossing;
}

function medalsFrom(authorMs: number): MedalTimes {
  const round = (v: number): number => Math.ceil(v / 10) * 10;
  return {
    author: authorMs,
    gold: round(authorMs * MEDALS.gold),
    silver: round(authorMs * MEDALS.silver),
    bronze: round(authorMs * MEDALS.bronze),
  };
}

export function trackFingerprint(t: TrackData): string {
  return hashHex(
    JSON.stringify([
      t.version,
      t.n,
      t.length,
      t.px,
      t.py,
      t.elev,
      t.curbLeft,
      t.curbRight,
      t.barrierLeft,
      t.barrierRight,
      t.spawnDistance,
      t.spawnSpeed,
      // v2 hashes stay exactly as they were.
      ...(t.version >= 3 ? [t.spawnOffset ?? 0, t.bank ?? null] : []),
      // Runoff aprons and surfaces (circuits from RUNOFF_FROM): they change the lap, so the hash covers them.
      ...(t.apronLeft ? [t.apronLeft, t.apronRight, t.apronWidthLeft, t.apronWidthRight, t.runoffLeft, t.runoffRight] : []),
    ]),
  );
}

export function generateCircuit(
  date: string,
  opts: {
    salt?: number;
    physics?: PhysicsTuning;
    spec?: DailySpec;
    era?: EraId;
    /** Runoff dressed for another car than the one choosing the layout: the car that will race it, on its day (a swapped day). */
    dress?: { era: EraId; date: string };
  } = {},
): GeneratedCircuit {
  // A day that races another date's circuit (calendar.ts): that circuit as it was made, with this day's car.
  const from = circuitDate(date);
  if (from !== date && !opts.salt && !opts.spec && !opts.physics) {
    const raced = racedCircuit(date, from, opts.era);
    if (raced) return raced;
  }
  const salt = opts.salt ?? opts.spec?.salt ?? 0;
  const base = opts.spec ?? defaultSpec(date, salt);
  if (opts.era && opts.era !== base.era) return previewCircuit(date, salt, base, opts.era);
  const spec = base;
  const p = opts.physics ?? eraPhysics(spec.era, date);
  // The day a handling change comes in keeps the circuit it already had: its layout is still chosen
  // with the old handling, then the bot sets the medals with the new.
  const lp = opts.physics ?? (date === HANDLING_2_FROM ? eraPhysics(spec.era, '2026-09-28') : date === HANDLING_3_FROM ? eraPhysics(spec.era, '2026-10-05') : date === HANDLING_31_FROM ? eraPhysics(spec.era, '2026-10-06') : date === HANDLING_32_FROM ? eraPhysics(spec.era, '2026-10-08') : date === HANDLING_33_FROM ? eraPhysics(spec.era, '2026-10-13') : date === HANDLING_34_FROM ? eraPhysics(spec.era, '2026-10-11') : p);
  // One size for every car: a faster car shows up as a faster lap.
  const scale = TRACK.layoutScale;
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const rejections: Record<string, number> = {};
  let botRuns = 0;
  const name = spec.name ?? generateCircuitName(Rng.fromString(`name|${date}|${salt}`), settingOfCircuit(date));

  const version = generatorVersion(date);
  const laps = lapWindow(version);
  // v3: the day's showpiece; the crossover makes it a figure of eight (a plain loop only if none works out).
  const signature = version >= 3 ? signatureOfCircuit(date) : null;
  const dressEra = opts.dress?.era ?? spec.era;
  const dressPhysics = opts.dress ? eraPhysics(opts.dress.era, opts.dress.date) : lp;
  for (let attempt = 0; attempt < TRACK.maxAttempts; attempt++) {
    const rng = Rng.fromString(`circuit|${date}|${salt}|${attempt}`);
    try {
      const fallback = signature === 'crossover' && attempt >= 250;
      const family: LayoutFamily = signature === 'crossover' && !fallback ? 'figure8' : 'loop';
      const cand = signature ? tryAttemptV3(rng, lp, scale, family, fallback ? 'stadium' : signature) : tryAttempt(rng, lp, scale);
      const runoff = version >= 3 && date >= RUNOFF_FROM;
      const track = buildTrackData(cand.cl, cand.corners, cand.straights, cand.zones, cand.features, cand.profile, cand.hills, version, cand.crossing ?? null, cand.line.offset, {
        bank: cand.bank,
        sections: cand.sections,
        signature: cand.signature,
        runoffStyle: runoff ? runoffStyle(dressEra) : undefined,
      });
      if (runoff) {
        // A probe lap at the bot's most committed pace: wherever its tyres reach the white line gets an apron
        // (the tyres of the car that will race it).
        const reach = botReach(buildRuntime(track, simHooksFor(spec, track)), dressPhysics, cand.line);
        const aprons = planAprons(track, reach, cand.profile, runoffStyle(dressEra));
        track.apronLeft = aprons.left;
        track.apronRight = aprons.right;
        track.apronWidthLeft = aprons.widthLeft;
        track.apronWidthRight = aprons.widthRight;
      }
      const modReason = validateModifiers(spec, track);
      if (modReason) throw new Rejected('modifier:' + modReason);
      const runtime = buildRuntime(track, simHooksFor(spec, track));
      let suite = runBotSuite(runtime, lp, cand.line);
      botRuns += suite.runs.length;
      if (!suite.best) throw new Rejected('bot-failed');
      const lapS = suite.best.lapMs / 1000;
      if (lapS < laps.min) throw new Rejected('lap-too-short');
      if (lapS > laps.max) throw new Rejected('lap-too-long');
      if (lp !== p) {
        suite = runBotSuite(runtime, p, cand.line);
        botRuns += suite.runs.length;
        if (!suite.best) throw new Rejected('bot-failed');
      }
      const t1 = typeof performance !== 'undefined' ? performance.now() : Date.now();
      return {
        date,
        salt,
        spec,
        attempt,
        archetype: cand.archetype,
        era: spec.era,
        physics: p,
        name,
        controlPoints: cand.cps,
        track,
        runtime,
        line: cand.line,
        bot: suite.best,
        medals: medalsFrom(suite.best.lapMs),
        trackHash: trackFingerprint(track),
        stats: { attempts: attempt + 1, rejections, botRuns, millis: t1 - t0 },
      };
    } catch (e) {
      if (!(e instanceof Rejected)) throw e;
      rejections[e.reason] = (rejections[e.reason] ?? 0) + 1;
    }
  }
  throw new Error(`No valid circuit for ${date} after ${TRACK.maxAttempts} attempts: ${JSON.stringify(rejections)}`);
}

/**
 * The circuit of another date (`from`, built for its own day, its own car choosing the layout), raced on `date`
 * with that day's car: the bot laps it again with this car for the author time and medals. Null if that car's
 * bot can't lap it (the day then gets a circuit of its own).
 */
function racedCircuit(date: string, from: string, era?: EraId): GeneratedCircuit | null {
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const spec: DailySpec = { ...defaultSpec(date), era: era ?? eraForDate(date) };
  // Its own day's build (never swapped back, and with the rotation's car whatever that day races now), its
  // runoff dressed for this day's car.
  const made = generateCircuit(from, { salt: 0, spec: { ...defaultSpec(from), era: rotationEra(from) }, dress: { era: spec.era, date } });
  const p = eraPhysics(spec.era, date);
  const runtime = buildRuntime(made.track, simHooksFor(spec, made.track));
  const suite = runBotSuite(runtime, p, made.line);
  if (!suite.best) return null;
  const t1 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return {
    ...made,
    date,
    spec,
    era: spec.era,
    physics: p,
    runtime,
    bot: suite.best,
    medals: medalsFrom(suite.best.lapMs),
    stats: { ...made.stats, botRuns: made.stats.botRuns + suite.runs.length, millis: t1 - t0 },
  };
}

/**
 * Dev preview: another era's car on the day's own circuit, so cars compare
 * lap for lap. Only the bot (author time, medals) reruns with that car; if its
 * bot can't lap the day's layout, the car gets a layout of its own instead.
 */
function previewCircuit(date: string, salt: number, base: DailySpec, era: EraId): GeneratedCircuit {
  const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  const day = generateCircuit(date, salt ? { salt, spec: base } : {});
  const p = eraPhysics(era, date);
  const suite = runBotSuite(day.runtime, p, day.line);
  if (!suite.best) return generateCircuit(date, { salt, spec: { ...base, era } });
  const t1 = typeof performance !== 'undefined' ? performance.now() : Date.now();
  return {
    ...day,
    spec: { ...base, era },
    era,
    physics: p,
    bot: suite.best,
    medals: medalsFrom(suite.best.lapMs),
    stats: { ...day.stats, botRuns: day.stats.botRuns + suite.runs.length, millis: t1 - t0 },
  };
}

/** Dev/diagnostics: one attempt's raw geometry and why it was rejected (if it was). */
export interface AttemptDiagnosis {
  cps: Vec2[];
  px: number[];
  py: number[];
  kappa: number[];
  reason: string | null;
  corners: Corner[];
  features: TrackFeatures | null;
  lineX: number[];
  lineY: number[];
  botMs: number | null;
  /** The built candidate (when it got that far), for bot debugging. */
  track?: TrackData;
  line?: RacingLine;
}

export function diagnoseAttempt(date: string, salt: number, attempt: number, p: PhysicsTuning = DEFAULT_PHYSICS, withBot = false, scale = 1): AttemptDiagnosis {
  const cps = makeControlPoints(Rng.fromString(`circuit|${date}|${salt}|${attempt}`), scale);
  const dense = sampleClosedCatmullRom(cps, 80);
  const cl = buildCenterline(resampleClosed(dense, TRACK.sampleSpacing));
  const out: AttemptDiagnosis = {
    cps,
    px: cl.px,
    py: cl.py,
    kappa: cl.kappa,
    reason: null,
    corners: findCorners(cl, TRACK.cornerCurvature, Math.round(12 / (cl.length / cl.n)), 0.12),
    features: null,
    lineX: [],
    lineY: [],
    botMs: null,
  };
  try {
    const cand = tryAttempt(Rng.fromString(`circuit|${date}|${salt}|${attempt}`), p, scale);
    out.cps = cand.cps;
    out.px = cand.cl.px;
    out.py = cand.cl.py;
    out.kappa = cand.cl.kappa;
    out.corners = cand.corners;
    out.features = cand.features;
    out.lineX = cand.line.x;
    out.lineY = cand.line.y;
    const track = buildTrackData(cand.cl, cand.corners, cand.straights, cand.zones, cand.features, cand.profile, cand.hills);
    out.track = track;
    out.line = cand.line;
    if (withBot) {
      const suite = runBotSuite(buildRuntime(track), p, cand.line);
      if (!suite.best) throw new Rejected('bot-failed');
      out.botMs = suite.best.lapMs;
      if (suite.best.lapMs / 1000 < TRACK.lapTimeMin) throw new Rejected('lap-too-short');
      if (suite.best.lapMs / 1000 > TRACK.lapTimeMax) throw new Rejected('lap-too-long');
    }
  } catch (e) {
    if (!(e instanceof Rejected)) throw e;
    out.reason = e.reason;
  }
  return out;
}
